export const en = {
  common: {
    add: "Add",
    signOut: "Sign out",
    noPhoto: "No photo",
    unassigned: "Unassigned",
    noRecordsHereYet: "No records here yet.",
    recordsCount: (n: number) => `${n} ${n === 1 ? "record" : "records"}`,
    roleAdmin: "admin",
    roleUser: "user",
    paginationPrevious: "Previous",
    paginationNext: "Next",
    pageOf: (page: number, total: number) => `Page ${page} of ${total}`,
  },
  types: {
    CD: "CD",
    CASSETTE: "Cassette",
    VINYL: "Vinyl",
  },
  nav: {
    collection: "Collection",
    artists: "Artists",
    browse: "Browse",
    books: "Books",
    settings: "Settings",
    profileSettings: "Profile & settings",
  },
  notFound: {
    eyebrow: "Not in the catalog",
    title: "This page isn't on the shelf.",
    body: "The link may be out of date, or the address has a typo. Your collection is untouched. Nothing was removed.",
    home: "Back to home",
    contactPrefix: "Still can't find it?",
    shelfAlt: "A shelf of record spines with one empty slot",
  },
  // Shown only when the native sign-in handoff lands in a browser instead of
  // the app — see src/app/native/auth/page.tsx. Deliberately says nothing
  // about codes or verifiers: the reader needs to know they are signed in and
  // what to do next, and the URL that brought them here is not something to
  // draw attention to.
  nativeAuthReturn: {
    eyebrow: "Sign-in complete",
    title: "You're signed in.",
    body: "This page opened in your browser instead of the Rakemori app. Nothing is wrong with your account — switch back to the app and choose Continue with Google once more to finish there.",
    browserNote: "Reading this on a computer? You're already signed in to this browser.",
    home: "Open my library",
    iosBody:
      "This page opened in Safari instead of the Rakemori app, and on iPhone trying again will land you right back here \u2014 this build cannot hand a Google sign-in back to the app. Nothing is wrong with your account. To use the app itself, open it and sign in with your username and password.",
    iosBodyNoPassword:
      "This page opened in Safari instead of the Rakemori app, and on iPhone trying again will land you right back here \u2014 this build cannot hand a Google sign-in back to the app. Nothing is wrong with your account: it just has no password yet, and a password is how you sign in inside the app.",
    iosAddPassword: "Add a password",
  },
  login: {
    subtitle: "Sign in to your library",
    username: "Username",
    password: "Password",
    signIn: "Sign in",
    signingIn: "Signing in…",
    invalidCredentials: "Invalid username or password",
    tooManyAttempts: "Too many sign-in attempts. Please wait a few minutes and try again.",
    orDivider: "or",
    signInWithGoogle: "Continue with Google",
    signInWithApple: "Continue with Apple",
    googleNotLinked: "This Google account isn't linked to a Rakemori account yet. Sign in below, then link Google from Settings.",
    appleNotLinked: "This Apple account isn't linked to a Rakemori account yet. Sign in below, then link Apple from Settings.",
    emailAlreadyRegistered:
      "An account already exists for this email. Sign in below, then link Google from Settings.",
    registrationClosed: "Registration is currently closed.",
    googleEmailUnverified:
      "Google didn't confirm an email address for that account, so we can't create one here.",
    registrationRateLimited: "Too many sign-up attempts. Try again later.",
    registrationFailed: "Something went wrong creating your account. Please try again.",
  },
  // The two screens /welcome resolves to: a brand-new OAuth sign-up settling
  // its address and its first password, and an account created before every
  // account had to hold one being caught up. Errors returned BY the endpoint
  // stay English literals and are shown verbatim — the API layer is not
  // dictionary-driven (see the note in oauth-unlink.ts); `failed` is the
  // fallback for a response that carried no message at all.
  welcome: {
    setupTitle: "Set up your account",
    addressIntro: (username: string) =>
      `Your collection lives at rakemori.com/${username}. You can change it later in Settings.`,
    passwordTitle: "Add a password",
    passwordIntro:
      "Your account signs in with Google or Apple only. Adding a password means you can still get in if you ever lose access to that account — there's no reset email here.",
    username: "Username",
    password: "Password",
    confirmPassword: "Confirm password",
    policyHint: (min: number) => `At least ${min} characters, with a capital letter and a number.`,
    continueAction: "Continue",
    saving: "Saving…",
    signOut: "Sign out",
    passwordMismatch: "Those passwords don't match.",
    failed: "Couldn't finish setting up your account.",
  },
  offline: {
    title: "You're offline",
    body: "Rakemori needs a network connection to load your catalog. Check your connection and try again.",
    browseOffline: "Browse your library offline",
    heading: "Offline library",
    syncedAt: "Synced",
    notDownloaded:
      "No offline copy on this device yet. Open the app once while online and it will download automatically.",
    searchPlaceholder: "Search titles, artists…",
    empty: "Nothing matches.",
    backOnline: "You're back online — reload to return to the app.",
    reload: "Reload",
    partialImages: "Storage was full — some full-size images aren't available offline.",
    all: "All",
    itemsTab: "Collection",
    booksTab: "Books",
    browseTab: "Browse",
    detail: {
      tracklist: "Tracklist",
      notes: "Notes",
      catalogNotes: "Catalog notes",
      location: "Location",
      borrowedBy: "Borrowed by",
      media: "Media",
      sleeve: "Sleeve",
      streamingOffline: "Streaming links need a connection",
    },
    browse: {
      artists: "Artists",
      genres: "Genres",
      styles: "Styles",
      countries: "Countries",
    },
  },
  landing: {
    nav: {
      what: "What it does",
      how: "How it works",
      where: "Where things live",
      app: "Android app",
      faq: "FAQ",
      dashboard: "Dashboard",
    },
    hero: {
      eyebrow: "Private catalog",
      title: "Rakemori",
      tagline: "Know what you have, and where it is.",
      body: "Rakemori is a private catalog for your CDs, cassettes, vinyl, and books. It keeps track of where each one is, who borrowed it, and what you've added or let go.",
      formatCd: "CDs",
      formatCs: "Cassettes",
      formatVi: "Vinyl",
      formatBk: "Books",
      searchPlaceholder: "Search title or artist…",
      scan: "Scan",
    },
    features: {
      heading: "Built for the way collections grow.",
      highlight: {
        badge: "New",
        title: "Hear it before you shelve it",
        body: "Scan a record and every track gets a play button before you save. The 30-second clips come from iTunes and Deezer, so you can check the match is right before it lands in your library. The play buttons stay on the tracklist afterwards.",
        nowPlaying: "Now playing",
      },
      items: [
        {
          title: "Cataloging",
          body: "Every CD, cassette, record, and book in one place. Add items by hand, or let the details fill themselves in.",
        },
        {
          title: "Barcode scan & auto-fill",
          body: "Scan a barcode or search by title and artist. Cover art and tracklists come from Discogs, Open Library, and Google Books.",
        },
        {
          title: "Physical location tracking",
          body: "Build a nested map of where things live, from the house down to the shelf it sits on.",
        },
        {
          title: "Sharing, borrowing & history",
          body: "Share your library read-only, and keep a record of what's on loan and to whom.",
        },
      ],
    },
    how: {
      heading: "From box to catalog.",
      steps: [
        {
          title: "Scan",
          body: "Point your camera at a barcode, or search by title and artist. The cover art and tracklist come back filled in, with a play button on every track.",
        },
        {
          title: "Catalog",
          body: "It lands in your library. Edit anything or add a note; it's your record now.",
        },
        {
          title: "Locate",
          body: "Tag where it lives, down to the shelf or the box, so you can find it later.",
        },
      ],
    },
    where: {
      eyebrow: "A nested map",
      heading: "Nothing gets lost in a box again.",
      body: "Build a location tree as deep as your home is: house, room, shelf, crate. Every item points to exactly where it sits, so you know where to put it back.",
      badges: {
        house: "House",
        livingRoom: "Living Room",
        shelfB: "Shelf B",
      },
      tree: {
        house: "House",
        livingRoom: "Living Room",
        shelfB: "Shelf B",
        shelfBItems: "42 items",
        cabinet: "Cabinet",
        cabinetItems: "16 items",
        study: "Study",
        boxes: "Boxes 1–3",
        boxesItems: "31 items",
        loft: "Loft",
        crate: "Crate",
        crateItems: "27 items",
      },
    },
    stats: {
      stat1: "Formats: CD · Cassette · Vinyl · Book",
      stat2: "Three auto-fill sources",
      stat3: "Private library, yours alone",
    },
    privacy: {
      eyebrow: "Private by default",
      heading: "Private by default, shared when you choose.",
      body: "Your library is private and belongs to you. You decide whether anyone else ever sees it.",
      items: [
        {
          title: "Nothing public",
          body: "There are no public listings. Nothing is visible until you say so.",
        },
        {
          title: "Read-only sharing",
          body: "Send a link that can be viewed but not edited. Revoke it any time.",
        },
        {
          title: "Loans & full history",
          body: "See who has what, and look back at everything you've added or let go.",
        },
      ],
    },
    faq: {
      eyebrow: "Questions",
      heading: "Good to know.",
      items: [
        {
          q: "Which formats can I catalog?",
          a: "CDs, cassettes, vinyl records, and books, all in the same library.",
        },
        {
          q: "Where do the details come from?",
          a: "Cover art, tracklists, and metadata come from Discogs, Open Library, and Google Books.",
        },
        {
          q: "Can other people see my library?",
          a: "Only if you share it. Shared links are read-only and can be revoked whenever you like.",
        },
        {
          q: "Do I need an account?",
          a: "Yes. Sign up with your Google account, or ask for a username and password. Your library is private either way, and only you can open it.",
        },
        {
          q: "What about things I lend out?",
          a: "Mark an item as on loan and to whom; the full borrowing history stays with the record.",
        },
        {
          q: "What if I remove something?",
          a: "It leaves your active shelves but stays in your history, so you always know what you once had.",
        },
        {
          q: "Is there a mobile app?",
          a: "Yes. Rakemori is on Google Play for Android, and it works in any browser on the phone or desktop. The iPhone app is on its way to the App Store. Same account, same library.",
        },
      ],
    },
    getApp: {
      eyebrow: "On Google Play",
      heading: "Rakemori, in your pocket.",
      body: "The Android app carries your whole catalog to the shelf you are standing in front of, with the barcode scanner one tap away. It signs in to the same library you use on the web.",
      cta: "Get it on Google Play",
      ctaSub: "Free · Android 7.0 and up",
      apkCta: "Download APK",
      apkNote:
        "For phones without the Play Store. Android asks for permission before it installs, and you come back here when there is a new version.",
      webCta: "Or open it in any browser",
      altStoreHeading: "iPhone, through AltStore",
      altStoreCta: "Copy source URL",
      altStoreCopied: "Copied",
      altStoreSelected: "Selected — copy it",
      altStoreNote:
        "Paste this into AltStore's Sources tab. AltStore signs the app with your own Apple ID, so the first install needs a computer running AltServer, and a free Apple ID has to refresh it every seven days. Links to rakemori.com open in Safari rather than in the app.",
      altStoreGuide: "Step-by-step install guide",
      ios: "App Store · coming soon",
      phoneAlt: "The Rakemori app running on an Android phone",
      points: [
        {
          title: "Scan with your camera",
          body: "Point at a barcode and the cover art, artist, and tracklist come back filled in.",
        },
        {
          title: "One library, both places",
          body: "What you add on the phone is already there when you open Rakemori on a laptop.",
        },
        {
          title: "Sign in your way",
          body: "Sign in with Google, or with a username and password. Everything stays private.",
        },
      ],
    },
    footer: {
      title: "Keep track of what's on your shelves.",
      meta: "RAKEMORI · PRIVATE CATALOG",
      googlePlay: "Google Play",
      instagram: "Rakemori on Instagram",
    },
  },
  appShowcase: {
    back: "Back to Rakemori",
    eyebrow: "Live preview",
    heading: "A closer look at Rakemori",
    body: "Every screen here comes from a working library. Sign in, browse a shelf, open a record, and see who borrowed what.",
    captions: {
      login: "Sign in",
      collection: "Your shelf",
      detail: "Record detail",
      borrowed: "Borrowed",
    },
    ctaEyebrow: "Try it yourself",
    ctaHeading: "Look around inside.",
    ctaBody: "Sign in with a live trial account. You can browse a real library, but you cannot change anything in it.",
    username: "Username",
    password: "Password",
    ctaButton: "Try it now",
  },
  collection: {
    searchPlaceholder: "Search title, artist, or track",
    ownerTitle: (name: string) => `${name}'s collection`,
    all: "All",
    noItemsYet: "No items yet",
    noMatches: "No matches",
    thisCollectionEmpty: "This collection is empty.",
    importCta: "Have a Discogs collection? Import it all at once",
    tryDifferentSearch: "Try a different search or filter.",
    sortAriaLabel: "Sort by",
    sortRecentlyAdded: "Recently added",
    sortArtist: "Artist",
    sortAlbumName: "Album name",
    sortAlbumYear: "Album year",
  },
  artists: {
    countLabel: (n: number) => `${n} ${n === 1 ? "artist" : "artists"} in this collection`,
    noRecordsYet: "No records yet",
    albumsOwned: "Albums Owned",
    noRecordsByArtist: "No records by this artist yet.",
  },
  browse: {
    title: "Browse by location",
    subtitle: "See what's stored where.",
    noLocationsYet: "No locations set up yet.",
  },
  filterPage: {
    noRecordsGenre: "No records with this genre.",
    noRecordsCountry: "No records from this country.",
    noRecordsStyle: "No records with this style.",
  },
  itemDetail: {
    release: "Release",
    tracklist: "Tracklist",
    findingPreviews: "Finding previews…",
    findMissingPreviews: (n: number) =>
      `Find ${n} missing ${n === 1 ? "preview" : "previews"}`,
    rescanPreviews: "Rescan previews",
    findingMissingPreviews: "Asking the music services…",
    previewsFilled: (n: number) => `Found ${n} ${n === 1 ? "preview" : "previews"}.`,
    previewsStillMissing: (n: number) =>
      `${n} ${n === 1 ? "track has" : "tracks have"} no preview on iTunes or Deezer.`,
    previewsRescanFailed: "Could not reach the music services. Try again in a moment.",
    refreshTracklist: "Refresh tracklist from Discogs",
    refreshingTracklist: "Fetching from Discogs…",
    tracklistRefreshed: "Tracklist updated from Discogs.",
    tracklistUnchanged: "Already matches Discogs.",
    tracklistRefreshFailed: "Could not reach Discogs. Try again in a moment.",
    listenOn: "Listen on",
    companies: "Companies",
    credits: "Credits",
    photos: "Photos",
    notes: "Notes",
    personalNotes: "Personal notes",
    location: "Location",
    price: "Price",
    barcode: "Barcode",
    condition: "Condition",
    otherCopies: "Other copies",
    copy: "Copy",
    mediaShort: "Media",
    sleeveShort: "Sleeve",
    country: "Country",
    genre: "Genre",
    style: "Style",
    released: "Released",
    thisRelease: "This release",
    originalRelease: "Original release",
    label: "Label",
    originallyYear: (year: number) => `originally ${year}`,
  },
  itemActions: {
    edit: "Edit",
    remove: "Remove",
    removing: "Removing…",
    removeTitle: "Remove this item?",
    removeMessage: "It will be removed from your collection. This can't be undone.",
    removeFailed: "Failed to remove. Please try again.",
  },
  itemForm: {
    uploading: "Uploading…",
    tapToChangeCover: "Tap to change cover",
    lookupLabel: "Search Discogs — album, artist, or both (great for releases without a barcode)",
    lookupPlaceholder: "e.g. Sore Centralismo",
    search: "Search",
    releaseFallback: "Release",
    loading: "Loading…",
    use: "Use",
    searchFailed: "Search failed — try again in a moment.",
    searchNoMatch: "No matches on Discogs — fill in the details manually.",
    couldNotLoad: "Couldn't load that release — try another result.",
    filledIn: "Filled in from Discogs — review and correct anything before saving.",
    barcodeLabel: "Barcode / UPC",
    scan: "Scan",
    stop: "Stop",
    lookUp: "Look up",
    noMatch: "No match found — fill in the details manually.",
    titleLabel: "Title",
    artistLabel: "Artist",
    typeLabel: "Type",
    thisReleaseLabel: "This release",
    yearPlaceholder: "Year",
    originalReleaseLabel: "Original release",
    countryLabel: "Country",
    releasedLabel: "Released (date)",
    releasedPlaceholder: "e.g. 2025-09-24",
    genreLabel: "Genre",
    genrePlaceholder: "Jazz, Rock…",
    styleLabel: "Style",
    stylePlaceholder: "Fusion…",
    priceLabel: "Price",
    currencyLabel: "Currency",
    mediaConditionLabel: "Media condition",
    sleeveConditionLabel: "Sleeve condition",
    notGraded: "Not graded",
    locationLabel: "Location",
    unassigned: "Unassigned",
    unassignedDefault: (label: string) => `Unassigned (defaults to ${label})`,
    labelsLabel: "Labels (one per line: Name – Catalog #)",
    labelsPlaceholder: "Alfa – MHJL 449",
    companiesLabel: "Companies (one per line: Role – Name)",
    companiesPlaceholder: "Recorded At – Studio Name",
    creditsLabel: "Credits (one per line: Role – Name)",
    creditsPlaceholder: "Bass – Artist Name",
    catalogNotesLabel: "Release notes (from Discogs)",
    notesLabel: "Personal notes",
    photosLabel: (n: number, max: number) => `Photos of the record (${n}/${max})`,
    processing: "Processing…",
    photosHint: "Snap the actual disc, sleeve, or inserts. Photos are converted to WebP to save space.",
    addPhoto: "Add",
    tracklistHeading: "Tracklist",
    addTrack: "Add track",
    trackTitlePlaceholder: "Track title",
    trackArtistPlaceholder: "Artist (compilations)",
    trackDurationPlaceholder: "mm:ss",
    findPreviews: "Find previews",
    findingPreviews: "Finding previews…",
    previewsNone: "No previews found for this tracklist.",
    saving: "Saving…",
    saveChanges: "Save changes",
    addToCollection: "Add to collection",
    readOnlyNotice: "This is a read-only trial account — you can search and scan, but changes can't be saved.",
    saveFailed: "Failed to save item",
    uploadFailed: "Failed to upload photo",
    photoUploadFailed: "Failed to upload a photo — please try again.",
    photoProcessFailed: "Couldn't process a photo — please try a different image.",
    maxPhotos: (max: number) => `You can add up to ${max} photos.`,
    tooManyPhotos: (max: number, skipped: number) => `Only ${max} photos allowed — ${skipped} skipped.`,
    viewExisting: "View it in your collection",
    alreadyInLibrary: "Already in your library",
    batchScan: "Batch",
    identifySleeve: "Identify from sleeve",
    sleeveCatnoMatches: (catno: string) => `Matches for catalogue number ${catno}`,
    sleeveTitleMatches: "Best guesses from the sleeve text",
    sleeveNoText: "Couldn't read any text in that photo.",
    sleeveNoMatch: "No Discogs match for that sleeve.",
    sleeveCameraFailed: "Couldn't open the camera.",
    matchRunout: "Match runout",
    runoutMatched: (matrix: string) => `Runout matched \u2014 ${matrix}. Best match first.`,
    runoutNoMatch: "No runout match among these results.",
    runoutFailed: "Runout match failed \u2014 try again in a moment.",
    addAnyway: "Add anyway \u2014 different copy",
    willAddCopy: "Will be added as a second copy.",
  },
  books: {
    title: "Books",
    searchPlaceholder: "Search title, author, or series",
    booksCount: (n: number) => `${n} ${n === 1 ? "book" : "books"}`,
    all: "All",
    noBooksYet: "No books yet",
    thisShelfEmpty: "Your book collection is empty.",
    noMatches: "No matches",
    tryDifferentSearch: "Try a different search or filter.",
    noBooksHereYet: "No books here yet.",
    formats: {
      HARDCOVER: "Hardcover",
      PAPERBACK: "Paperback",
      OTHER: "Other",
    },
    sortAuthor: "Author",
    sortTitle: "Title",
    sortYear: "Year",
    addTitle: "Add a book",
    editTitle: (title: string) => `Edit ${title}`,
    detail: {
      publisher: "Publisher",
      published: "Published",
      firstPublished: "First published",
      source: "Source",
      originalTitle: "Original title",
      language: "Language",
      translator: "Translator",
      edition: "Edition",
      series: "Series",
      seriesVolume: "Volume",
      pages: "Pages",
      isbn: "ISBN",
      subjects: "Subjects",
      description: "Description",
      details: "Details",
      photos: "Photos",
      personalNotes: "Personal notes",
      location: "Location",
      price: "Price",
    },
    removeTitle: "Remove this book?",
    removeMessage: "It will be removed from your collection. This can't be undone.",
    noBooksSubject: "No books on this subject yet.",
    form: {
      lookupLabel: "Search Open Library / Google Books by title, author, or both",
      lookupPlaceholder: "e.g. Bumi Manusia",
      search: "Search",
      use: "Use",
      loading: "Loading…",
      filledIn: "Filled in from the lookup. Review and correct anything before saving.",
      noMatch: "No match found. Fill in the details manually.",
      searchFailed: "Search failed. Try again in a moment.",
      couldNotLoad: "Couldn't load that result. Try another one.",
      isbnLabel: "ISBN / barcode",
      scan: "Scan",
      stop: "Stop",
      lookUp: "Look up",
      batchScan: "Batch",
      identifyCover: "Identify from cover",
      coverNoText: "Couldn't read any text in that photo.",
      coverIsbnMatch: (isbn: string) => `Found by ISBN ${isbn} — tap to use it`,
      coverTitleMatches: "Best guesses from the cover text",
      coverNoMatch: "No match for that cover.",
      titleLabel: "Title",
      subtitleLabel: "Subtitle",
      authorLabel: "Author",
      formatLabel: "Format",
      publishedYearLabel: "Published (year)",
      publisherLabel: "Publisher",
      languageLabel: "Language",
      pagesLabel: "Pages",
      subjectsLabel: "Subjects",
      subjectsPlaceholder: "History, Fiction…",
      descriptionLabel: "Description",
      collectorHeading: "Collector details",
      collectorHint: "All optional, for cataloging specific editions.",
      originalTitleLabel: "Original title",
      originalYearLabel: "First published (year)",
      translatorLabel: "Translator",
      editionLabel: "Edition / printing",
      editionPlaceholder: "e.g. 2nd ed., 3rd printing",
      seriesLabel: "Series",
      seriesVolumeLabel: "Volume",
      priceLabel: "Price",
      currencyLabel: "Currency",
      locationLabel: "Location",
      unassigned: "Unassigned",
      unassignedDefault: (label: string) => `Unassigned (defaults to ${label})`,
      notesLabel: "Personal notes",
      photosLabel: (n: number, max: number) => `Photos of the book (${n}/${max})`,
      photosHint: "Snap the actual copy: cover wear, inscriptions, slipcase. Photos are converted to WebP to save space.",
      processing: "Processing…",
      addPhoto: "Add",
      uploading: "Uploading…",
      tapToChangeCover: "Tap to change cover",
      viewExisting: "View it in your collection",
      saveChanges: "Save changes",
      addToCollection: "Add to collection",
      saving: "Saving…",
      saveFailed: "Failed to save book",
      uploadFailed: "Failed to upload photo",
      photoProcessFailed: "Couldn't process a photo. Please try a different image.",
      tooManyPhotos: (max: number, skipped: number) => `Only ${max} photos allowed, so ${skipped} did not upload.`,
      maxPhotos: (max: number) => `You can add up to ${max} photos.`,
    },
  },
  authors: {
    booksOwned: "Books Owned",
    noBooksByAuthor: "No books by this author yet.",
  },
  people: {
    all: "All",
    artists: "Artists",
    authors: "Authors",
    searchPlaceholder: "Search artists & authors",
    peopleCount: (n: number) => `${n} ${n === 1 ? "person" : "people"} in this collection`,
    artistsCount: (n: number) => `${n} ${n === 1 ? "artist" : "artists"} in this collection`,
    authorsCount: (n: number) => `${n} ${n === 1 ? "author" : "authors"} in this collection`,
    artistTag: "Artist",
    authorTag: "Author",
    noPeopleYet: "No artists or authors yet",
  },
  confirmDialog: {
    cancel: "Cancel",
    confirm: "Confirm",
  },
  settings: {
    eyebrow: "Account",
    title: "Settings",
    profile: "Profile",
    appearance: "Appearance",
    library: "Library",
    locations: "Locations",
    history: "History",
    borrowed: "Borrowed",
    language: "Language",
    help: "Help",
    locationsDescription:
      "Build the nested tree of where things are physically stored, for example House → Living Room → TV Cabinet → Rack A1.",
  },
  profile: {
    // Shown under a connected account that is the only way into the account.
    // The server refuses the unlink regardless; this explains the disabled
    // button so it doesn't read as a bug.
    lastCredential: "This is the only way you can sign in. Set a password before unlinking it.",
    usernameLabel: "Username",
    usernameHint: (username: string) => `Your library's address: rakemori.com/${username}`,
    nameLabel: "Name",
    nameHint: "Shown next to your photo and on your shared library.",
    usernameTaken: "That username is taken.",
    usernameInvalid: "Only letters, numbers, and hyphens, and not a reserved word.",
    renameTitle: "Change your username?",
    renameMessage: (from: string, to: string) =>
      `Your library moves from rakemori.com/${from} to rakemori.com/${to}. Links to the old address keep working and send people to the new one, and nobody else can take the old name.`,
    renameConfirm: "Change username",
    saveFailed: "Couldn't save your profile.",
  },
  history: {
    description: "Every record and book you've added or removed, newest first. Only you can see this.",
    added: "Added",
    removed: "Removed",
    emptyState: "Nothing here yet. Add or remove a record or book and it'll show up in this list.",
    reasonSold: (price: string) => `Sold · Rp ${price}`,
    reasonSoldNoPrice: "Sold",
    reasonExchanged: "Exchanged for",
    reasonExchangedUnknown: "a removed record",
  },
  removalReason: {
    title: "Why is this being removed?",
    missing: "Missing",
    trash: "Trash",
    sold: "Sold",
    exchange: "Exchange",
    others: "Others",
    skip: "Skip",
    soldPriceLabel: "Price (optional)",
    othersPlaceholder: "Add a note (optional)",
    exchangeSearchPlaceholder: "Search your library…",
    exchangeSearching: "Searching…",
    exchangeNoResults: "No matches. Add a new one instead.",
    exchangeAddNewRecord: "+ Add a new record instead",
    exchangeAddNewBook: "+ Add a new book instead",
    exchangeSelected: (title: string) => `Selected: ${title}`,
    exchangeChange: "Change",
    exchangeResumeFailed:
      "Record added, but the original couldn't be removed automatically. Remove it manually if needed.",
    exchangeResumeDismiss: "Dismiss",
  },
  borrow: {
    dialogTitle: "Who's borrowing this?",
    nameLabel: "Borrower name",
    namePlaceholder: "e.g. Sarah",
    phoneLabel: "Phone (optional)",
    phonePlaceholder: "e.g. 08123456789",
    searching: "Searching…",
    cancel: "Cancel",
    confirm: "Confirm",
    markReturned: "Mark returned",
    marking: "Marking…",
    borrowedDaysAgo: (n: number) =>
      n === 0 ? "Borrowed today" : `Borrowed ${n} ${n === 1 ? "day" : "days"} ago`,
    description: "Records and books currently out on loan, oldest first. Only you can see this.",
    emptyState: "Nothing out on loan right now.",
    borrowRequiredError: "Borrower info is required. Please try again.",
  },
  appearance: {
    description: "Choose how the app looks on this device. Auto follows your system light/dark setting.",
    light: "Light",
    dark: "Dark",
    auto: "Auto",
  },
  language: {
    description: "Choose the app's display language.",
    english: "English",
    indonesian: "Bahasa Indonesia",
  },
  share: {
    loading: "Loading…",
    heading: "Share with people",
    description:
      "Give someone with a Rakemori account read access to your private library. They sign in with their own account, so there is no extra password to hand out, and you can revoke it any time.",
    usernamePlaceholder: "Their username",
    shareButton: "Share",
    sharing: "Sharing…",
    emptyShares: "Not shared with anyone yet.",
    addFailed: "Couldn't share with that person.",
    revokeTitle: (who: string) => `Stop sharing with ${who}?`,
    revokeMessage: "They'll immediately lose access to your library. You can share again later.",
    revokeConfirm: "Stop sharing",
    sharedWithYouHeading: "Shared with you",
    sharedWithYouDescription: "Private libraries other people have shared with you.",
    someonesCollection: (who: string) => `${who}'s collection`,
  },
  backup: {
    title: "Backup & restore",
    description:
      "Download a complete backup of your collection as a single file, including every record and book, their photos, and your location tree. Keep it somewhere safe, or use it to move your collection to another account.",
    download: "Download backup",
    importHeading: "Restore from a backup",
    mergeLabel: "Merge",
    replaceLabel: "Replace",
    mergeHint:
      "Adds records from the backup to your current collection, skipping any you already have. Reuses matching locations instead of duplicating them.",
    replaceHint:
      "Wipes your current collection and replaces it entirely with the backup. Can't be undone.",
    chooseFile: "Choose backup file",
    importButton: "Restore",
    importing: "Restoring…",
    replaceConfirmTitle: "Replace your whole collection?",
    replaceConfirmMessage:
      "Every record and location you have now will be permanently deleted and replaced with the contents of this backup. This can't be undone.",
    replaceConfirmLabel: "Replace everything",
    resultSummary: (records: number, books: number, skipped: number, images: number) =>
      `Restored ${records} ${records === 1 ? "record" : "records"} and ${books} ${books === 1 ? "book" : "books"}` +
      (skipped > 0 ? `, skipped ${skipped} duplicate${skipped === 1 ? "" : "s"}` : "") +
      `, and ${images} ${images === 1 ? "image" : "images"}.`,
    error: "Import failed. Please check the file and try again.",
  },
  help: {
    intro:
      "A quick guide to everything in Rakemori. Tap a section to expand it.",
    sections: [
      {
        title: "Getting started",
        body: "Rakemori helps you catalog and locate your CDs, cassettes, and vinyl records. Add records manually, by scanning a barcode, or by searching Discogs. Cover art, tracklist, and other details fill in automatically when a match is found.",
      },
      {
        title: "Adding & editing records",
        body: "Tap Add on the Collection page to create a new record. Scan a barcode or search Discogs by album/artist to auto-fill details, or enter everything by hand. From any record's page, use Edit to change details or Remove to delete it. Removing a record can't be undone.",
      },
      {
        title: "Removing a record or book",
        body: "When you remove a record or book, you're asked why: Missing, Trash, Sold, Exchanged for another item, or Others. The reason is saved to your Activity history. Choosing Exchange lets you search your library for what you're trading it for, or add a new record/book on the spot; the old one is automatically removed once the new one is saved.",
      },
      {
        title: "Activity history",
        body: "Settings → History keeps a private log of every record or book you've added or removed, newest first, visible only to you. Added entries link back to the record; removed entries still show what it was, even though it's gone.",
      },
      {
        title: "Searching, filtering & sorting",
        body: "The search bar on the Collection page filters results as you type. It matches title, artist, and track names. Use the All/CD/Cassette/Vinyl pills to filter by format, and the sort icon to order by recently added, artist, album name, or year.",
      },
      {
        title: "Artists & authors",
        body: "The Artists tab is one browse-people page for the whole library: use the All / Artists / Authors pills to switch, and the search bar to filter by name as you type. Artist cards show photos and bios from Discogs; author cards show photos and bios from Open Library. Tap anyone to see their profile plus every album or book you own by them.",
      },
      {
        title: "Books",
        body: "The Books tab catalogs your physical books alongside the music. Add one by scanning its ISBN barcode (the same scanner as records, since a book's barcode is its ISBN) or by searching title/author; details, cover, and subjects fill in from Open Library, with Google Books as a fallback. Collector fields (edition, translator, series, original title/year) are all optional. Filter by Hardcover/Paperback, sort, and tap a subject on any book to see everything you own on that subject.",
      },
      {
        title: "Locations & Browse",
        body: "Organize where records are physically stored in Settings → Locations. Build a nested tree like House → Living Room → TV Cabinet → Rack A1. The Browse tab lets anyone explore your collection by that same location tree. Set a default location so new records are placed somewhere sensible automatically.",
      },
      {
        title: "Lending out a record or book",
        body: "Move a record or book's Location to \"Borrowed\" and save. You'll be asked for the borrower's name (autocompleted from anyone who's borrowed before in this library) and phone number. Settings → Borrowed lists everything currently out on loan, oldest first, with a one-tap \"Mark returned\" that shelves it back where it came from. Changing a borrowed item's Location away from \"Borrowed\" through the normal edit form also silently marks it returned.",
      },
      {
        title: "Hiding a location from public",
        body: "In Settings → Locations, tap the eye icon next to any location to hide it, along with everything stored inside it, from public visitors and guests, even when your library is set to Public. Only you (and the site admin) still see it. Hiding a parent location hides everything nested inside it too.",
      },
      {
        title: "Sharing your library",
        body: "In Settings → Library, set your library to Public to let anyone view it at your profile URL without logging in. Or keep it Private and share it with specific people: enter the username of anyone with a Rakemori account and they'll be able to view your library while signed in as themselves. There is no extra password to pass around, and you can revoke access per person at any time. Libraries others share with you appear under \"Shared with you\" in the same place. The public view toggles control whether price and location are visible to people who aren't you.",
      },
      {
        title: "Appearance & language",
        body: "Settings → Appearance lets you switch between Light, Dark, or Auto (follows your device). Settings → Language switches the app between English and Bahasa Indonesia instantly. Before you've picked one, new visitors see whichever of the two their browser prefers.",
      },
      {
        title: "Keeping Discogs data fresh",
        body: "Settings → Library has a \"Rescan library from Discogs\" button that re-fetches title, artist, year, genre, label, credits, and more for every record matched to Discogs. This is handy for backfilling records added before these fields existed. It only touches your own library and paces itself to stay within Discogs's rate limit.",
      },
      {
        title: "Backup & restore",
        body: "Settings → Library → Backup & restore downloads your whole collection as a single .zip file, including records, books, photos, and location tree. To bring it back, choose the file and pick Merge (adds the backup's records and books to what you have, skipping duplicates) or Replace (wipes your collection and restores the backup exactly). Replace can't be undone, so keep your backup file somewhere safe.",
      },
    ],
  },
  install: {
    iphone: {
      metaTitle: "Install on iPhone",
      metaDescription:
        "Two ways to put Rakemori on an iPhone: the app through AltStore and AltServer, or the web app from Safari with Add to Home Screen.",
      back: "Back to Rakemori",
      eyebrow: "iPhone",
      title: "Put Rakemori on your iPhone",
      intro:
        "Rakemori isn't on the App Store yet. Until it is, you have two options: install the app through AltStore, which needs a computer, or add the web app from Safari, which doesn't. Either way you sign in to the same library.",
      compare: {
        heading: "Which one?",
        appColumn: "AltStore app",
        webColumn: "Safari web app",
        rows: [
          { label: "Needs a computer", app: "Yes, for the first install and every refresh", web: "No" },
          { label: "Expires", app: "Every seven days on a free Apple ID", web: "Never" },
          { label: "Sign in with Google", app: "Yes", web: "Yes" },
          { label: "Barcode scanning", app: "Built in, with the phone camera", web: "Through the browser camera" },
          { label: "Works offline", app: "Your library, once it has loaded", web: "Your library, once it has loaded" },
        ],
        advice:
          "If you're not sure, go with the web app. It takes a minute and there's nothing to keep up with. You can come back for AltStore later if you want the built-in scanner.",
      },
      app: {
        heading: "Option 1: the app, through AltStore",
        intro:
          "AltStore installs apps that aren't on the App Store by signing them with your own Apple ID. The signing happens on a Mac or Windows PC running AltServer, so you need one nearby for the first install. Have a cable and your Apple ID password ready. Allow about ten minutes.",
        steps: [
          {
            title: "Install AltServer on your computer",
            body: [
              "Get it from [altstore.io](https://altstore.io). On a Mac, drag AltServer into Applications and open it. It sits in the menu bar rather than the Dock. On Windows, run the installer, and make sure iTunes and iCloud came from Apple's own website rather than the Microsoft Store, or AltServer won't see your phone.",
            ],
          },
          {
            title: "Plug in your iPhone and install AltStore",
            body: [
              "Connect the phone with a cable and tap [[Trust]] if it asks. Click the AltServer icon in the menu bar (Mac) or system tray (Windows), choose [[Install AltStore]], and pick your iPhone. Sign in with your Apple ID when asked. AltServer only uses it to sign apps. It never reaches Rakemori.",
            ],
          },
          {
            title: "Trust it on your iPhone",
            body: [
              "AltStore is now on your Home Screen, but iOS won't open it until you say so. Go to [[Settings]] → [[General]] → [[VPN & Device Management]], tap the entry with your Apple ID, and tap [[Trust]].",
              "If the phone asks you to turn on [[Developer Mode]], go ahead: [[Settings]] → [[Privacy & Security]] → [[Developer Mode]], then restart when prompted. Every sideloaded app needs this.",
            ],
          },
          {
            title: "Add the Rakemori source",
            body: [
              "Open AltStore, go to the [[Sources]] tab, tap [[+]] and paste this address exactly as it is:",
              "`{sourceUrl}`",
              "Tap [[Add Source]] and Rakemori appears in the list.",
            ],
          },
          {
            title: "Install Rakemori",
            body: [
              "Go to [[Browse]], find Rakemori and tap [[Free]]. AltStore signs the app with your Apple ID and installs it. Keep the phone on the same Wi-Fi as the computer while it works. Once the icon shows up on your Home Screen, you're done.",
              "Sign in the way you always do, with Google or with your username and password.",
            ],
          },
        ],
        refresh: {
          title: "Keeping it alive",
          body: [
            "A free Apple ID signs the app for **seven days**. After that it won't open until it's refreshed. AltStore does this on its own in the background whenever the phone is on the same Wi-Fi as a computer running AltServer, so leaving AltServer running is usually enough. You can also open AltStore and tap [[Refresh All]]. A paid Apple Developer account stretches the seven days to a year.",
          ],
        },
        caveats: {
          title: "Worth knowing",
          body: [
            "A free Apple ID holds **three** sideloaded apps at a time, counting every source you've added. Rakemori uses one of those slots.",
            "Links to rakemori.com open in Safari rather than in the app, so a shared library link will not jump straight into it. Signing in with Google is unaffected and works here.",
            "Signing in with Apple is not offered yet, on the app or on the web.",
            "If you later install the App Store version, delete the AltStore one first. iOS won't keep both.",
            "Needs iOS {minOs} or later.",
          ],
        },
      },
      web: {
        heading: "Option 2: the web app, from Safari",
        intro:
          "Rakemori was built to run as a web app. Added from Safari it gets its own icon, opens full screen without the address bar, and stays signed in. Nothing expires and there's nothing to refresh.",
        steps: [
          {
            title: "Open the sign-in page in Safari",
            body: [
              "It has to be Safari. Chrome and other browsers can't add a web app to the Home Screen. Go to:",
              "`{loginUrl}`",
              "Sign in however you normally do, Google and Apple included.",
            ],
          },
          {
            title: "Tap Share",
            body: ["It's the square with the arrow pointing up, in Safari's toolbar."],
          },
          {
            title: "Choose Add to Home Screen",
            body: [
              "Scroll down the share sheet until you find [[Add to Home Screen]]. If it isn't there, tap [[Edit Actions]] at the bottom and switch it on.",
            ],
          },
          {
            title: "Turn on Open as Web App",
            body: [
              "On the next screen, check that [[Open as Web App]] is on. Off, you get a bookmark that opens in Safari. On, Rakemori opens as its own app. Older versions of iOS don't show the toggle and do this by default.",
            ],
          },
          {
            title: "Tap Add",
            body: [
              "Rakemori is now on your Home Screen. Open it from there rather than from Safari and it runs full screen with your session already in place.",
            ],
          },
        ],
        fallback: {
          title: "If it opens in Safari anyway",
          body: [
            "The toggle was off. Hold the icon, choose [[Delete Bookmark]], and repeat from step 1 with [[Open as Web App]] switched on.",
          ],
        },
      },
      help: {
        heading: "Still stuck?",
        body: "Email [hello@rakemori.com](mailto:hello@rakemori.com) with your iPhone model, iOS version and which option you were on.",
      },
    },
  },
};
